import { AxiosInstance } from 'axios';
import {
  ApiResponse,
  CreateFolderRequest,
  CreateFolderResponse,
  ListFoldersResponse,
  ListFoldersWithOwnerResponse,
  GenerateUploadUrlRequest,
  GenerateUploadUrlResponse,
  GenerateDownloadUrlRequest,
  GenerateDownloadUrlResponse,
  BatchDownloadUrlRequest,
  BatchDownloadUrlResponse,
  GenerateSignedLinkRequest,
  GenerateSignedLinkResponse,
  ListFilesResponse,
  ListFilesWithOwnerResponse,
  ListFilesQueryParams,
  DeleteFileResponse,
  DeleteFolderResponse,
  ListUsersResponse,
  UpdateUserRolesRequest,
  UpdateUserRolesResponse,
  BudgetDashboardResponse,
  BudgetDashboardQueryParams,
  PerUserCostResponse,
  PerUserCostQueryParams,
  ExportCostReportResponse,
} from '../../../shared/types';
import api from '../utils/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = api; // Use the configured axios instance with token management
  }

  // Folder operations
  async createFolder(request: CreateFolderRequest): Promise<CreateFolderResponse> {
    const response = await this.client.post<ApiResponse<CreateFolderResponse>>('/folders', request);
    return response.data.data!;
  }

  async listFolders(): Promise<ListFoldersResponse> {
    const response = await this.client.get<ApiResponse<ListFoldersResponse>>('/folders');
    return response.data.data!;
  }

  async deleteFolder(folderId: string): Promise<DeleteFolderResponse> {
    const response = await this.client.delete<ApiResponse<DeleteFolderResponse>>(`/folders/${folderId}`);
    return response.data.data!;
  }

  // File operations
  async generateUploadUrl(request: GenerateUploadUrlRequest): Promise<GenerateUploadUrlResponse> {
    const response = await this.client.post<ApiResponse<GenerateUploadUrlResponse>>('/files/upload-url', request);
    return response.data.data!;
  }

  async generateDownloadUrl(request: GenerateDownloadUrlRequest): Promise<GenerateDownloadUrlResponse> {
    const response = await this.client.post<ApiResponse<GenerateDownloadUrlResponse>>('/files/download-url', request);
    return response.data.data!;
  }

  async generateBatchDownloadUrl(request: BatchDownloadUrlRequest): Promise<BatchDownloadUrlResponse> {
    const response = await this.client.post<ApiResponse<BatchDownloadUrlResponse>>('/files/batch-download-url', request);
    return response.data.data!;
  }

  async generateSignedLink(request: GenerateSignedLinkRequest): Promise<GenerateSignedLinkResponse> {
    const response = await this.client.post<ApiResponse<GenerateSignedLinkResponse>>('/files/signed-link', request);
    return response.data.data!;
  }

  async listFiles(params?: ListFilesQueryParams): Promise<ListFilesResponse> {
    const response = await this.client.get<ApiResponse<ListFilesResponse>>('/files', { params });
    return response.data.data!;
  }

  async deleteFile(fileId: string): Promise<DeleteFileResponse> {
    const response = await this.client.delete<ApiResponse<DeleteFileResponse>>(`/files/${fileId}`);
    return response.data.data!;
  }

  // Admin operations
  async listUsers(): Promise<ListUsersResponse> {
    const response = await this.client.get<ApiResponse<ListUsersResponse>>('/admin/users');
    return response.data.data!;
  }

  async updateUserRoles(userId: string, request: UpdateUserRolesRequest): Promise<UpdateUserRolesResponse> {
    const response = await this.client.put<ApiResponse<UpdateUserRolesResponse>>(`/admin/users/${userId}/roles`, request);
    return response.data.data!;
  }

  async adminDeleteFile(fileId: string): Promise<DeleteFileResponse> {
    const response = await this.client.delete<ApiResponse<DeleteFileResponse>>(`/admin/files/${fileId}`);
    return response.data.data!;
  }

  async adminDeleteFolder(folderId: string): Promise<DeleteFolderResponse> {
    const response = await this.client.delete<ApiResponse<DeleteFolderResponse>>(`/admin/folders/${folderId}`);
    return response.data.data!;
  }

  // Budget operations
  async getBudgetDashboard(params?: BudgetDashboardQueryParams): Promise<BudgetDashboardResponse> {
    const response = await this.client.get<ApiResponse<BudgetDashboardResponse>>('/admin/budget/dashboard', { params });
    return response.data.data!;
  }

  async getPerUserCosts(params?: PerUserCostQueryParams): Promise<PerUserCostResponse> {
    const response = await this.client.get<ApiResponse<PerUserCostResponse>>('/admin/budget/users', { params });
    return response.data.data!;
  }

  async exportCostReport(format: 'csv' | 'json' = 'csv'): Promise<ExportCostReportResponse> {
    const response = await this.client.get<ApiResponse<ExportCostReportResponse>>('/admin/budget/export', {
      params: { format }
    });
    return response.data.data!;
  }

  // Storage management operations
  async listAllFiles(params?: ListFilesQueryParams): Promise<ListFilesWithOwnerResponse> {
    const response = await this.client.get<ApiResponse<ListFilesWithOwnerResponse>>('/admin/files', { params });
    return response.data.data!;
  }

  async listAllFolders(): Promise<ListFoldersWithOwnerResponse> {
    const response = await this.client.get<ApiResponse<ListFoldersWithOwnerResponse>>('/admin/folders');
    return response.data.data!;
  }
}

export const apiService = new ApiService();